import { useState, useEffect, memo } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { 
  MessageCircle, 
  Cake, 
  Gift, 
  Star, 
  Heart, 
  Sparkles,
  ChefHat,
  Users,
  Phone,
  MapPin,
  Send,
  Menu,
  X
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";

// Brand Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ea683c0-b123-4fb1-bdab-12b339dc716c/artifacts/mc7knc3b_987FA5DA-9A91-4195-A3E8-415CFA814BAC.png";
const WHATSAPP_NUMBER = "5511973822831";

// Gallery Images - Organized: Bolos primeiro, Ovos de Páscoa depois
const GALLERY_IMAGES = [
  // BOLOS
  {
    url: "https://customer-assets.emergentagent.com/job_8ea683c0-b123-4fb1-bdab-12b339dc716c/artifacts/z23t3yqg_3614BA0F-7E42-40CD-8798-2F03BFF8B22E.png",
    alt: "Bolo decorado com frutas vermelhas"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_8ea683c0-b123-4fb1-bdab-12b339dc716c/artifacts/c80yw3ij_65273BC5-C3AB-4708-97FF-EDD704528AB9.png",
    alt: "Bolo tema Tardezinha"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_encomendas-doces/artifacts/8mtuozfv_6EF3F877-7203-4698-A1CE-79F183BC3D15.png",
    alt: "Bolo de chocolate com cereja"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_encomendas-doces/artifacts/qsb0tjww_0B69B8FB-33D3-4A2C-AD37-5AFB1C0EAD4F.png",
    alt: "Bolo infantil rosa Maria Alice"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_encomendas-doces/artifacts/sav5mp1r_AEB35891-CD31-4B38-A652-DE8E1CE26DCF.png",
    alt: "Bolo colorido Giovanna"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_encomendas-doces/artifacts/fi6xbtti_CA132FF7-9BBD-4D38-9346-560325D7DFEF.png",
    alt: "Bolo tropical Julia"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_encomendas-doces/artifacts/iz9yxfv2_AC1B8BBD-2C4A-4578-A1D9-57D713838B7A.png",
    alt: "Bolo azul Fábio"
  },
  // OVOS DE PÁSCOA
  {
    url: "https://customer-assets.emergentagent.com/job_encomendas-doces/artifacts/vaama9qm_sivone%20profissional%20ovo%20de%20colher%20kinder.png",
    alt: "Ovo de colher Kinder com chocolates e morangos"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_8ea683c0-b123-4fb1-bdab-12b339dc716c/artifacts/zztyf3p0_232428E2-3086-44CB-94CB-C5CB49CB01CE.png",
    alt: "Ovo de Páscoa Kinder recheado"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_8ea683c0-b123-4fb1-bdab-12b339dc716c/artifacts/78k8qzkr_E8CCC665-C6A5-4347-87C9-9D46DBF254A2.png",
    alt: "Ovos de Páscoa embalados"
  }
];

// Categories
const CATEGORIES = [
  { title: "Bolos Personalizados", description: "Criados especialmente para você", icon: Cake },
  { title: "Bolos para Aniversário", description: "Comemore com sabor e beleza", icon: Gift },
  { title: "Ovos de Páscoa Artesanais", description: "Recheios especiais e únicos", icon: Heart },
  { title: "Encomendas Especiais", description: "Para ocasiões memoráveis", icon: Star },
  { title: "Doces sob Encomenda", description: "Delícias feitas com amor", icon: Sparkles },
  { title: "Salgados para Festas", description: "Qualidade para seu evento", icon: ChefHat }
];

// Differentials
const DIFFERENTIALS = [
  { title: "Produção Artesanal", icon: ChefHat },
  { title: "Atendimento Personalizado", icon: Users },
  { title: "Capricho em Cada Detalhe", icon: Heart },
  { title: "Para Momentos Especiais", icon: Star },
  { title: "Direto pelo WhatsApp", icon: Phone }
];

// Simple fade animation - optimized
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Header Component - Memoized
const Header = memo(({ scrolled, onMenuClick, isMenuOpen }) => (
  <header
    className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-[#FCFBF8]/95 backdrop-blur-md shadow-sm border-b border-[#6A3A69]/10' 
        : 'bg-[#FCFBF8]'
    }`}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-20">
        <a href="#hero" data-testid="header-logo-link">
          <img 
            src={LOGO_URL} 
            alt="Sivone Encomendas" 
            className="h-14 w-auto object-contain"
            data-testid="header-logo"
            loading="eager"
          />
        </a>
        
        <a href="#pedido" className="hidden md:flex" data-testid="header-cta-link">
          <Button 
            className="bg-[#6A3A69] hover:bg-[#5D3A5F] text-white px-6 py-2 rounded-full font-medium flex items-center gap-2 shadow-sm transition-colors"
            data-testid="header-cta-btn"
          >
            <MessageCircle size={18} />
            Fazer Encomenda
          </Button>
        </a>

        <button 
          className="md:hidden text-[#6A3A69] p-2"
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
          aria-label="Menu"
        >
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>
    </div>

    {/* Mobile Menu */}
    {isMenuOpen && (
      <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-[#EBE1E8]">
        <div className="px-4 py-6 space-y-4">
          <a href="#galeria" onClick={onMenuClick} className="block py-2 text-[#4A3B49] hover:text-[#6A3A69] font-medium" data-testid="mobile-nav-galeria">Galeria</a>
          <a href="#categorias" onClick={onMenuClick} className="block py-2 text-[#4A3B49] hover:text-[#6A3A69] font-medium" data-testid="mobile-nav-categorias">Categorias</a>
          <a href="#diferenciais" onClick={onMenuClick} className="block py-2 text-[#4A3B49] hover:text-[#6A3A69] font-medium" data-testid="mobile-nav-diferenciais">Diferenciais</a>
          <a href="#pedido" onClick={onMenuClick} data-testid="mobile-nav-pedido">
            <Button className="w-full bg-[#6A3A69] hover:bg-[#5D3A5F] text-white py-3 rounded-full font-medium flex items-center justify-center gap-2">
              <MessageCircle size={18} />
              Fazer Encomenda
            </Button>
          </a>
        </div>
      </div>
    )}
  </header>
));

Header.displayName = 'Header';

// Hero Section - Simplified animations
const HeroSection = () => (
  <section 
    id="hero" 
    className="min-h-screen flex flex-col items-center justify-center text-center pt-20 px-4 bg-[#FCFBF8]"
    data-testid="hero-section"
  >
    <div className="max-w-4xl mx-auto">
      <img
        src={LOGO_URL}
        alt="Sivone Encomendas"
        className="w-48 sm:w-64 md:w-72 h-auto mx-auto mb-8"
        data-testid="hero-logo"
        loading="eager"
      />
      
      <h1 
        className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2E1C2D] mb-6 leading-tight"
        data-testid="hero-title"
      >
        Encomendas artesanais para{" "}
        <span className="text-[#6A3A69]">momentos especiais</span>
      </h1>
      
      <p 
        className="text-base sm:text-lg text-[#4A3B49] mb-8 max-w-2xl mx-auto leading-relaxed"
        data-testid="hero-subtitle"
      >
        Bolos, ovos de Páscoa e encomendas feitas com carinho, capricho e sabor 
        para deixar sua comemoração ainda mais especial.
      </p>
      
      <div
        className="flex items-center justify-center gap-2 text-[#8A7B88] mb-8"
        data-testid="hero-location"
      >
        <MapPin size={16} className="text-[#D4AF37]" />
        <span>Guarulhos/SP</span>
      </div>
      
      <a href="#pedido" data-testid="hero-cta-link">
        <Button 
          className="bg-[#6A3A69] hover:bg-[#5D3A5F] text-white px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg rounded-full font-medium flex items-center justify-center gap-3 shadow-lg hover:shadow-xl mx-auto transition-all duration-200"
          data-testid="hero-cta-btn"
        >
          <MessageCircle size={22} />
          <span>Fazer Encomenda no WhatsApp</span>
        </Button>
      </a>
    </div>
  </section>
);

// Gallery Image Component - Memoized with lazy loading
const GalleryImage = memo(({ image, index }) => (
  <motion.div
    className="relative overflow-hidden rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300 aspect-square bg-[#EBE1E8]"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-50px" }}
    variants={fadeIn}
    transition={{ delay: Math.min(index * 0.05, 0.3), duration: 0.4 }}
    data-testid={`gallery-image-${index}`}
  >
    <img
      src={image.url}
      alt={image.alt}
      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
      loading="lazy"
      decoding="async"
    />
  </motion.div>
));

GalleryImage.displayName = 'GalleryImage';

// Gallery Section
const GallerySection = () => (
  <section 
    id="galeria" 
    className="py-16 md:py-24 px-4 bg-white"
    data-testid="gallery-section"
  >
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 
          className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6A3A69] mb-4"
          data-testid="gallery-title"
        >
          Conheça algumas das nossas encomendas
        </h2>
        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {GALLERY_IMAGES.map((image, index) => (
          <GalleryImage key={index} image={image} index={index} />
        ))}
      </div>
    </div>
  </section>
);

// Category Card - Memoized
const CategoryCard = memo(({ category, index }) => (
  <motion.div
    className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-[#EBE1E8] hover:border-[#D4AF37]/50 transition-colors duration-200 flex flex-col items-center text-center"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-30px" }}
    variants={fadeIn}
    transition={{ delay: Math.min(index * 0.08, 0.4), duration: 0.4 }}
    data-testid={`category-card-${index}`}
  >
    <div className="w-14 h-14 rounded-full bg-[#FCFBF8] border border-[#D4AF37]/30 flex items-center justify-center text-[#6A3A69] mb-4">
      <category.icon size={26} />
    </div>
    <h3 className="font-serif text-lg font-semibold text-[#2E1C2D] mb-1">
      {category.title}
    </h3>
    <p className="text-[#8A7B88] text-sm">
      {category.description}
    </p>
  </motion.div>
));

CategoryCard.displayName = 'CategoryCard';

// Categories Section
const CategoriesSection = () => (
  <section 
    id="categorias" 
    className="py-16 md:py-24 px-4 bg-[#FCFBF8]"
    data-testid="categories-section"
  >
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 
          className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6A3A69] mb-4"
          data-testid="categories-title"
        >
          O que você pode encomendar
        </h2>
        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {CATEGORIES.map((category, index) => (
          <CategoryCard key={index} category={category} index={index} />
        ))}
      </div>
    </div>
  </section>
);

// Differentials Section
const DifferentialsSection = () => (
  <section 
    id="diferenciais" 
    className="py-16 md:py-24 px-4 bg-white"
    data-testid="differentials-section"
  >
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 
          className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6A3A69] mb-4"
          data-testid="differentials-title"
        >
          Por que escolher a Sivone Encomendas?
        </h2>
        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
        {DIFFERENTIALS.map((item, index) => (
          <motion.div
            key={index}
            className="flex flex-col items-center text-center gap-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-30px" }}
            variants={fadeIn}
            transition={{ delay: Math.min(index * 0.08, 0.4), duration: 0.4 }}
            data-testid={`differential-${index}`}
          >
            <div className="w-14 h-14 rounded-full bg-[#FCFBF8] border border-[#D4AF37]/30 flex items-center justify-center text-[#6A3A69]">
              <item.icon size={24} />
            </div>
            <h3 className="font-medium text-[#2E1C2D] text-sm md:text-base">
              {item.title}
            </h3>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Contact Form Section
const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    nome: '',
    bairro: '',
    encomenda: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const message = `Olá! Vim pelo site da Sivone Encomendas.

*Nome:* ${formData.nome}
*Bairro:* ${formData.bairro}
*Encomenda:* ${formData.encomenda}

Aguardo retorno!`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodedMessage}`;
    
    window.location.href = whatsappUrl;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isFormValid = formData.nome && formData.bairro && formData.encomenda;

  return (
    <section 
      id="pedido" 
      className="py-16 md:py-24 px-4 bg-[#FCFBF8]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6A3A69] mb-4"
            data-testid="contact-title"
          >
            Peça sua encomenda
          </h2>
          <p className="text-[#4A3B49] text-base md:text-lg max-w-xl mx-auto">
            Preencha seus dados e fale direto no WhatsApp para iniciar seu atendimento.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mt-4" />
        </div>

        <div className="max-w-xl mx-auto bg-white p-6 md:p-10 rounded-2xl shadow-sm border border-[#EBE1E8]">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-[#2E1C2D] mb-2">
                Seu nome
              </label>
              <Input
                id="nome"
                name="nome"
                type="text"
                placeholder="Digite seu nome completo"
                value={formData.nome}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border-[#EBE1E8] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 transition-colors"
                required
                data-testid="input-nome"
              />
            </div>

            <div>
              <label htmlFor="bairro" className="block text-sm font-medium text-[#2E1C2D] mb-2">
                Bairro / Onde mora
              </label>
              <Input
                id="bairro"
                name="bairro"
                type="text"
                placeholder="Ex: Vila Galvão, Centro, Pimentas..."
                value={formData.bairro}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border-[#EBE1E8] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 transition-colors"
                required
                data-testid="input-bairro"
              />
            </div>

            <div>
              <label htmlFor="encomenda" className="block text-sm font-medium text-[#2E1C2D] mb-2">
                O que deseja encomendar?
              </label>
              <Textarea
                id="encomenda"
                name="encomenda"
                placeholder="Ex: Bolo de chocolate para 20 pessoas, ovo de Páscoa recheado..."
                value={formData.encomenda}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border-[#EBE1E8] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 transition-colors resize-none"
                required
                data-testid="input-encomenda"
              />
            </div>

            <Button
              type="submit"
              disabled={!isFormValid}
              className={`w-full py-5 rounded-full font-medium text-base flex items-center justify-center gap-3 transition-all duration-200 ${
                isFormValid 
                  ? 'bg-[#6A3A69] hover:bg-[#5D3A5F] text-white shadow-md hover:shadow-lg'
                  : 'bg-[#EBE1E8] text-[#8A7B88] cursor-not-allowed'
              }`}
              data-testid="submit-btn"
            >
              <MessageCircle size={20} />
              Chamar no WhatsApp
              <Send size={16} />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Footer - Simplified
const Footer = () => (
  <footer className="py-8 px-4 bg-[#2E1C2D] text-white" data-testid="footer">
    <div className="max-w-7xl mx-auto text-center">
      <img 
        src={LOGO_URL} 
        alt="Sivone Encomendas" 
        className="h-14 w-auto mx-auto mb-3 brightness-0 invert opacity-80"
        data-testid="footer-logo"
        loading="lazy"
      />
      <div className="flex items-center justify-center gap-2 text-[#D4AF37] mb-3">
        <MapPin size={14} />
        <span className="text-sm">Guarulhos/SP</span>
      </div>
      <p className="text-[#8A7B88] text-xs">
        © {new Date().getFullYear()} Sivone Encomendas - Todos os direitos reservados
      </p>
    </div>
  </footer>
);

// Floating WhatsApp Button
const FloatingWhatsAppButton = () => (
  <a
    href="#pedido"
    className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BA5C] text-white p-4 rounded-full shadow-lg flex items-center justify-center transition-transform duration-200 hover:scale-110"
    data-testid="floating-whatsapp-btn"
    aria-label="Fazer encomenda no WhatsApp"
  >
    <MessageCircle size={26} />
  </a>
);

// Main App
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuClick = () => setIsMenuOpen(false);

  return (
    <div className="App bg-[#FCFBF8] min-h-screen" data-testid="app-container">
      <Header 
        scrolled={scrolled} 
        onMenuClick={() => setIsMenuOpen(!isMenuOpen)}
        isMenuOpen={isMenuOpen}
      />
      
      <main>
        <HeroSection />
        <GallerySection />
        <CategoriesSection />
        <DifferentialsSection />
        <ContactFormSection />
      </main>
      
      <Footer />
      <FloatingWhatsAppButton />
    </div>
  );
}

export default App;
